import React, { useState, useEffect, useCallback, useRef } from "react";
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTimesCircle, faDollarSign, faCrown, faBan, faPhone, faSpinner, faUserShield, faEnvelope, faBell } from '@fortawesome/free-solid-svg-icons';
import { Col, Row, Card, Table, Button, Badge, Spinner, Pagination, Modal, Form, Alert } from '@themesberg/react-bootstrap';
import { CircleChartWidget } from "../../components/Widgets";
import { trafficShares } from "../../data/charts";
import api from '../../utils/api';
import { Routes } from "../../routes";
import { resolveChatRoomId } from "../../utils/chatUtils";
import { useAuth } from '../../contexts/AuthContext';
import SendUserNotificationModal from '../../components/SendUserNotificationModal';
import { formatPurchaseDateTime } from '../../utils/dateTime';

function memberUid(member) {
  return member.userId || member.id;
}

export default () => {
  const { currentUser } = useAuth();
  const history = useHistory();

  // Firebase Auth Custom Claims State
  const [hasAdminClaim, setHasAdminClaim] = useState(null);
  const [loadingClaim, setLoadingClaim] = useState(true);
  const [claimMessage, setClaimMessage] = useState(null);

  const [totalUsers, setTotalUsers] = useState(0);
  const [memberStats, setMemberStats] = useState({
    today: 0,
    last24h: 0,
    last7d: 0,
    last30d: 0,
    total: 0
  });
  const [selectedStat, setSelectedStat] = useState('total');
  const [onlineUsers, setOnlineUsers] = useState(0);
  const [totalEarnings, setTotalEarnings] = useState(0);
  const [recentPurchases, setRecentPurchases] = useState([]);
  const [lastMembers, setLastMembers] = useState([]);
  const [onlineMembers, setOnlineMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalOnlineCount, setTotalOnlineCount] = useState(0);

  // Online üyeler için sayfalama
  const [currentOnlinePage, setCurrentOnlinePage] = useState(1);
  const itemsPerPage = 5;

  // Modal state'leri
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [showBanModal, setShowBanModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [targetUserId, setTargetUserId] = useState(null);
  const [targetUserName, setTargetUserName] = useState('');
  const [targetUserCredits, setTargetUserCredits] = useState(0);
  const [targetUserIsBanned, setTargetUserIsBanned] = useState(false);
  const [targetUserIsPremium, setTargetUserIsPremium] = useState(false);
  const [creditsInput, setCreditsInput] = useState("");
  const [premiumDaysInput, setPremiumDaysInput] = useState("");
  const [modalError, setModalError] = useState(null);
  const [showNotificationModal, setShowNotificationModal] = useState(false);

  // Mesaj gönderme modal state'leri
  const [fakeAccounts, setFakeAccounts] = useState([]);
  const [selectedFakeAccountId, setSelectedFakeAccountId] = useState(null);
  const [fakeAccountSearch, setFakeAccountSearch] = useState("");
  const [messageText, setMessageText] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);

  // Kazanç filtresi için tarih state'leri
  const [earningStartDate, setEarningStartDate] = useState("");
  const [earningEndDate, setEarningEndDate] = useState("");
  const [isFilteringEarnings, setIsFilteringEarnings] = useState(false);
  const [todayEarnings, setTodayEarnings] = useState(0);


  // Call başlatma modal state'leri
  const [showCallModal, setShowCallModal] = useState(false);
  const [callType, setCallType] = useState("recorded_voice");
  const [audioRecordings, setAudioRecordings] = useState([]);
  const [videoRecordings, setVideoRecordings] = useState([]);
  const [loadingRecordings, setLoadingRecordings] = useState(false);

  // Refs for modal focus
  const creditsInputRef = useRef(null);
  const premiumInputRef = useRef(null);

  // Coin settings
  const [coinSettings, setCoinSettings] = useState({
    messageCoin: 1,
    imageCoin: 20,
    videoCoin: 0,
    audioCoin: 0,
    callCoin: 150
  });

  
  const fetchStats = async () => {
    try {
      setLoading(true);
      const res = await api.get('/dashboard/stats');
      const data = res.data;
      
      setTotalUsers(data.totalUsers);
      setMemberStats(data.memberStats);
      setOnlineUsers(typeof data.onlineUsers === 'number' ? data.onlineUsers : 0);
      setTotalEarnings(data.totalEarnings);
      setTodayEarnings(data.todayEarnings);
      setRecentPurchases(data.recentPurchases);
      setLastMembers(data.lastMembers);
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadOnlineMembers = async () => {
    try {
      const res = await api.get(`/dashboard/online-members?page=${currentOnlinePage}&limit=${itemsPerPage}`);
      const total = res.data.total || 0;
      setOnlineMembers(res.data.members || []);
      setTotalOnlineCount(total);
      // Top "Online Üyeler" card stays aligned with the online list
      setOnlineUsers(total);
    } catch(err) {
      console.error('Error loading online members:', err);
    }
  };

  const loadCoinSettings = async () => {
    try {
      const res = await api.get('/dashboard/coin-settings');
      if (res.data) {
        setCoinSettings({
          messageCoin: res.data.messageCoin || 1,
          imageCoin: res.data.imageCoin || 20,
          videoCoin: res.data.videoCoin || 0,
          audioCoin: res.data.audioCoin || 0,
          callCoin: res.data.callCoin || 150
        });
      }
    } catch (err) {
      console.error('Error loading coin settings:', err);
    }
  };

  useEffect(() => {
    fetchStats();
    loadCoinSettings();
  }, [earningStartDate, earningEndDate]);

  useEffect(() => {
    loadOnlineMembers();
    
    // Setup polling for online members
    const interval = setInterval(() => {
      loadOnlineMembers();
    }, 30000); // 30 seconds
    
    return () => clearInterval(interval);
  }, [currentOnlinePage, itemsPerPage]);

  // Admin claim kontrolü
  useEffect(() => {
    const checkAdminClaim = async () => {
      try {
        if (currentUser) {
          const tokenResult = await currentUser.getIdTokenResult(true);
          setHasAdminClaim(tokenResult.claims.admin === true);
        }
      } catch (err) {
        console.error('Error checking admin claim:', err);
        setHasAdminClaim(false);
      } finally {
        setLoadingClaim(false);
      }
    };
    checkAdminClaim();
  }, [currentUser]);

  // Admin Claims Ayarlama
  const setAdminClaims = async () => {
    try {
      setLoadingClaim(true);
      setClaimMessage(null);
      await api.post('/users/set-admin-claims', { uid: currentUser.uid });
      // Token'ı yenile
      await currentUser.getIdToken(true);
      const tokenResult = await currentUser.getIdTokenResult(true);
      setHasAdminClaim(tokenResult.claims.admin === true);
      setClaimMessage({ type: 'success', text: 'Admin yetkisi başarıyla verildi!' });
    } catch (err) {
      console.error('Error setting admin claims:', err);
      setClaimMessage({ type: 'danger', text: 'Admin yetkisi verilemedi: ' + (err.response?.data?.error || err.message) });
    } finally {
      setLoadingClaim(false);
    }
  };

  // Online Üyeleri Sıfırla
  const handleResetOnlineUsers = async () => {
    if (!window.confirm('Tüm online üyelerin durumunu sıfırlamak istediğinize emin misiniz?')) return;
    try {
      setLoading(true);
      await api.post('/users/reset-online-status');
      setOnlineMembers([]);
      setTotalOnlineCount(0);
      setOnlineUsers(0);
      await Promise.all([fetchStats(), loadOnlineMembers()]);
      alert('Tüm online üyeler sıfırlandı!');
    } catch (err) {
      console.error('Error resetting online users:', err);
      alert('Online üyeler sıfırlanırken hata: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const totalOnlinePages = Math.ceil(totalOnlineCount / itemsPerPage);

  // Fake hesapları yükle
  const loadFakeAccounts = useCallback(async () => {
    try {
      const res = await api.get('/dashboard/fake-accounts');
      const accounts = res.data?.accounts || [];
      setFakeAccounts(accounts);
      if (accounts.length > 0 && !selectedFakeAccountId) {
        setSelectedFakeAccountId(accounts[0].id);
      }
    } catch (err) {
      console.error('Error loading fake accounts:', err);
    }
  }, [selectedFakeAccountId]);

  // Mesaj modal'ını aç
  const openMessageModal = async (userId, userName) => {
    setTargetUserId(userId);
    setTargetUserName(userName);
    setMessageText("");
    setFakeAccountSearch("");
    setModalError(null);
    await loadFakeAccounts();
    setShowMessageModal(true);
  };

  // Call modal'ını aç
  const openCallModal = async (userId, userName) => {
    setTargetUserId(userId);
    setTargetUserName(userName);
    setModalError(null);
    setCallType("normal_voice");
    setAudioRecordings([]);
    setVideoRecordings([]);
    try {
      const res = await api.get('/dashboard/fake-accounts');
      const accounts = res.data?.accounts || [];
      setFakeAccounts(accounts);
      const fakeId = selectedFakeAccountId || accounts[0]?.id || '';
      if (fakeId) {
        setSelectedFakeAccountId(fakeId);
      }
    } catch (err) {
      console.error('Error loading fake accounts:', err);
    }
    setShowCallModal(true);
  };

  // Kayıtları yükle (fake hesap için)
  const loadRecordings = async (fakeAccountId) => {
    if (!fakeAccountId) return;
    try {
      setLoadingRecordings(true);
      const res = await api.get(`/users/${fakeAccountId}/recordings`);
      setAudioRecordings(res.data?.audioRecordings || res.data?.audio || []);
      setVideoRecordings(res.data?.videoRecordings || res.data?.video || []);
    } catch (err) {
      console.error('Error loading recordings:', err);
      setModalError('Kayıtlar yüklenirken bir hata oluştu: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoadingRecordings(false);
    }
  };

  // Call başlatma fonksiyonu
  const handleStartCall = async () => {
    if (!targetUserId || !selectedFakeAccountId) {
      setModalError('Lütfen fake hesap seçin.');
      return;
    }
    try {
      setModalError(null);
      let mediaUrl = null;
      if (callType === 'recorded_voice') {
        if (audioRecordings.length === 0) { setModalError('Ses kaydı bulunamadı.'); return; }
        mediaUrl = audioRecordings[0].fileUrl;
      } else if (callType === 'recorded_video') {
        if (videoRecordings.length === 0) { setModalError('Video kaydı bulunamadı.'); return; }
        mediaUrl = videoRecordings[0].fileUrl;
      }

      // Hızlı cevapsız ile aynı admin arama uç noktası kullanılır.
      await api.post('/calls/admin-request', {
        callerId: selectedFakeAccountId,
        receiverId: targetUserId,
        type: callType === 'recorded_video' ? 'video' : 'voice',
        mediaUrl: mediaUrl || null,
        roomId: [selectedFakeAccountId, targetUserId].sort().join('_')
      });
      setShowCallModal(false);
      setModalError(null);
    } catch (err) {
      console.error('Error starting call:', err);
      setModalError('Arama başlatılırken bir hata oluştu: ' + (err.response?.data?.error || err.message));
    }
  };

  // Mesaj gönderme fonksiyonu
  const handleSendMessage = async (navigateToChat = false) => {
    if (!targetUserId || !selectedFakeAccountId || !messageText.trim()) {
      setModalError('Lütfen fake hesap seçin ve mesaj yazın.');
      return;
    }
    try {
      setSendingMessage(true);
      setModalError(null);
      
      const res = await api.post('/chat/send-message', {
        senderId: selectedFakeAccountId,
        receiverId: targetUserId,
        message: messageText.trim()
      });
      
      const calculatedRoomId = res.data.roomId;
      
      setShowMessageModal(false);
      setMessageText("");
      setFakeAccountSearch("");
      setSendingMessage(false);
      
      if (navigateToChat && calculatedRoomId) {
        history.push(`/messages/quick-unreplied?roomId=${calculatedRoomId}&receiverId=${selectedFakeAccountId}&senderId=${targetUserId}`);
      }
    } catch (err) {
      console.error('Error sending message:', err);
      const serverError = err.response?.data?.error || err.response?.data?.message;
      setModalError('Mesaj gönderilirken bir hata oluştu: ' + (serverError || err.message));
      setSendingMessage(false);
    }
  };

  // Modal açma fonksiyonları
  const patchMember = (uid, patch) => {
    setLastMembers((prev) => prev.map((m) => (m.userId === uid ? { ...m, ...patch } : m)));
    setOnlineMembers((prev) => prev.map((m) => (m.id === uid ? { ...m, ...patch } : m)));
  };

  const openCreditsModal = (userId, userName, currentCredits) => {
    if (!userId) {
      setModalError('Kullanıcı kimliği bulunamadı.');
      return;
    }
    setTargetUserId(userId);
    setTargetUserName(userName);
    setTargetUserCredits(currentCredits);
    setCreditsInput("");
    setModalError(null);
    setShowCreditsModal(true);
  };

  const openPremiumModal = (userId, userName, isPremium) => {
    if (!userId) {
      setModalError('Kullanıcı kimliği bulunamadı.');
      return;
    }
    setTargetUserId(userId);
    setTargetUserName(userName);
    setTargetUserIsPremium(!!isPremium);
    setPremiumDaysInput(isPremium ? "" : "30");
    setModalError(null);
    setShowPremiumModal(true);
  };

  const openNotificationModal = (userId, userName) => {
    if (!userId) return;
    setTargetUserId(userId);
    setTargetUserName(userName);
    setShowNotificationModal(true);
  };

  const openBanModal = async (userId, userName, isBanned) => {
    setTargetUserId(userId);
    setTargetUserName(userName);
    setTargetUserIsBanned(isBanned);
    setModalError(null);
    setShowBanModal(true);
  };

  // Modal işlem fonksiyonları
  const handleUpdateCredits = async () => {
    if (!targetUserId) return;
    try {
      const creditsChange = parseInt(creditsInput, 10);
      if (isNaN(creditsChange) || creditsChange === 0) {
        setModalError('Geçerli bir jeton değeri girin. Pozitif ekler, negatif çıkarır.');
        return;
      }
      const res = await api.post('/users/update-credits', {
        userId: targetUserId,
        creditsChange
      });
      const nextCredits = typeof res.data?.credits === 'number' ? res.data.credits : (targetUserCredits + creditsChange);
      patchMember(targetUserId, { credit: nextCredits });
      setTargetUserCredits(nextCredits);
      setShowCreditsModal(false);
      setCreditsInput("");
      setTargetUserId(null);
      setModalError(null);
      if (typeof fetchStats === 'function') fetchStats();
      if (typeof loadOnlineMembers === 'function') loadOnlineMembers();
    } catch (err) {
      console.error('Error updating credits:', err);
      setModalError('Jeton güncellenirken bir hata oluştu: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleUpdatePremium = async (daysOverride) => {
    if (!targetUserId) return;
    try {
      const days = daysOverride !== undefined ? daysOverride : parseInt(premiumDaysInput, 10);
      if (isNaN(days) || days < 0) {
        setModalError('Geçerli bir gün sayısı girin. VIP kaldırmak için 0 yazın.');
        return;
      }
      await api.post('/users/update-premium', {
        userId: targetUserId,
        days,
        isPremium: days > 0
      });
      patchMember(targetUserId, { isPremium: days > 0 });
      setShowPremiumModal(false);
      setPremiumDaysInput("");
      setTargetUserId(null);
      setModalError(null);
      if (typeof fetchStats === 'function') fetchStats();
      if (typeof loadOnlineMembers === 'function') loadOnlineMembers();
    } catch (err) {
      console.error('Error updating premium:', err);
      setModalError('Premium durumu güncellenirken bir hata oluştu: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleBanUser = async () => {
    if (!targetUserId) return;
    try {
      await api.post('/users/update-ban', {
        userId: targetUserId,
        isBanned: !targetUserIsBanned
      });
      setShowBanModal(false);
      setTargetUserId(null);
      setModalError(null);
      if (typeof fetchStats === 'function') fetchStats();
      if (typeof loadOnlineMembers === 'function') loadOnlineMembers();
    } catch (err) {
      console.error('Error banning user:', err);
      setModalError('Kullanıcı ban durumu güncellenirken bir hata oluştu.');
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <>
      <div className="py-4">
        <h4>Anasayfa</h4>
      </div>

      <Row className="mb-4">
        <Col xs={12} sm={6} lg={3} className="mb-4">
          <Card border="0" className="shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <h2 className="mb-0">
                  {selectedStat === 'total' && memberStats.total.toLocaleString('tr-TR')}
                  {selectedStat === 'today' && memberStats.today.toLocaleString('tr-TR')}
                  {selectedStat === 'last24h' && memberStats.last24h.toLocaleString('tr-TR')}
                  {selectedStat === 'last7d' && memberStats.last7d.toLocaleString('tr-TR')}
                  {selectedStat === 'last30d' && memberStats.last30d.toLocaleString('tr-TR')}
                </h2>
                <div className="btn-group">
                  <Badge
                    bg={selectedStat === 'total' ? 'primary' : 'light'}
                    className="cursor-pointer me-1 text-dark"
                    onClick={() => setSelectedStat('total')}
                    style={{ cursor: 'pointer' }}
                  >
                    Hepsi
                  </Badge>
                  <Badge
                    bg={selectedStat === 'today' ? 'primary' : 'light'}
                    className="cursor-pointer me-1 text-dark"
                    onClick={() => setSelectedStat('today')}
                    style={{ cursor: 'pointer' }}
                  >
                    Bugün
                  </Badge>
                  <Badge
                    bg={selectedStat === 'last24h' ? 'primary' : 'light'}
                    className="cursor-pointer me-1 text-dark"
                    onClick={() => setSelectedStat('last24h')}
                    style={{ cursor: 'pointer' }}
                  >
                    24s
                  </Badge>
                  <Badge
                    bg={selectedStat === 'last7d' ? 'primary' : 'light'}
                    className="cursor-pointer me-1 text-dark"
                    onClick={() => setSelectedStat('last7d')}
                    style={{ cursor: 'pointer' }}
                  >
                    7g
                  </Badge>
                  <Badge
                    bg={selectedStat === 'last30d' ? 'primary' : 'light'}
                    className="cursor-pointer text-dark"
                    onClick={() => setSelectedStat('last30d')}
                    style={{ cursor: 'pointer' }}
                  >
                    30g
                  </Badge>
                </div>
              </div>
              <p className="text-gray mb-0">
                {selectedStat === 'total' && 'Toplam Kullanıcı'}
                {selectedStat === 'today' && 'Bugünkü Yeni Üyeler'}
                {selectedStat === 'last24h' && 'Son 24 Saatteki Üyeler'}
                {selectedStat === 'last7d' && 'Son 7 Gündeki Üyeler'}
                {selectedStat === 'last30d' && 'Son 30 Gündeki Üyeler'}
              </p>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={6} lg={3} className="mb-4">
          <Card border="0" className="shadow-sm">
            <Card.Body>
              <h2 className="mb-1">{onlineUsers.toLocaleString('tr-TR')}</h2>
              <p className="text-gray mb-0">Online Üyeler</p>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={6} lg={6} className="mb-4">
          <Card border="0" className="shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div>
                  <h2 className="mb-0" style={{ fontSize: '34px' }}>
                    {isFilteringEarnings ? (
                      <Spinner animation="border" size="sm" className="me-2" />
                    ) : null}
                    {Math.floor(totalEarnings).toLocaleString('tr-TR')} TL
                  </h2>
                  <p className="text-gray mb-0">Sabah 6 Sonrası : {Math.floor(todayEarnings).toLocaleString('tr-TR')} TL</p>
                </div>
                <div className="d-flex gap-2 flex-wrap align-items-center">
                  <div className="d-flex align-items-center">
                    <small className="me-1 text-muted">Baş:</small>
                    <input
                      type="datetime-local"
                      className="form-control form-control-sm"
                      style={{ width: 'auto', fontSize: '0.75rem' }}
                      value={earningStartDate}
                      onChange={(e) => setEarningStartDate(e.target.value)}
                    />
                  </div>
                  <div className="d-flex align-items-center">
                    <small className="me-1 text-muted">Bit:</small>
                    <input
                      type="datetime-local"
                      className="form-control form-control-sm"
                      style={{ width: 'auto', fontSize: '0.75rem' }}
                      value={earningEndDate}
                      onChange={(e) => setEarningEndDate(e.target.value)}
                    />
                  </div>
                  {(earningStartDate || earningEndDate) && (
                    <Button
                      variant="link"
                      size="sm"
                      className="text-danger p-0 ms-1"
                      onClick={() => {
                        setEarningStartDate("");
                        setEarningEndDate("");
                      }}
                    >
                      Sıfırla
                    </Button>
                  )}
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col xs={12} className="mb-4">
          <Card border="0" className="shadow-sm">
            <Card.Header className="border-bottom">
              <h5 className="mb-0">Son Satın Alımlar</h5>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table className="table-dark table-hover mb-0" style={{ fontSize: '0.85rem' }}>
                  <thead>
                    <tr>
                      <th className="border-0">#</th>
                      <th className="border-0">Kullanıcı</th>
                      <th className="border-0">Email</th>
                      <th className="border-0">Ücret</th>
                      <th className="border-0">Önceki Kredi</th>
                      <th className="border-0">Şimdiki Kredi</th>
                      <th className="border-0">SKU</th>
                      <th className="border-0">Eklenen Kredi</th>
                      <th className="border-0">Durum</th>
                      <th className="border-0">Tarih</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentPurchases.length === 0 ? (
                      <tr>
                        <td colSpan="10" className="text-center p-4">Henüz satın alma yok.</td>
                      </tr>
                    ) : (
                      recentPurchases.map((purchase) => (
                        <tr key={purchase.id}>
                          <td>{purchase.id}</td>
                          <td>{purchase.user}</td>
                          <td>{purchase.email}</td>
                          <td>{purchase.credit}</td>
                          <td>{purchase.pastCredit}</td>
                          <td>{purchase.currentCredit}</td>
                          <td>{purchase.skuInfo || '-'}</td>
                          <td>{purchase.eklenenKredi}</td>
                          <td>
                            <Badge bg={purchase.status === 'Başarılı Alım' ? 'success' : (String(purchase.status || '').startsWith('HATA') ? 'danger' : 'warning')} className="badge-sm">
                              {purchase.status || 'Bilinmiyor'}
                            </Badge>
                          </td>
                          <td>{formatPurchaseDateTime(purchase.date)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>


      <Row>
        <Col xs={12} lg={6} className="mb-4">
          <Card border="0" className="shadow-sm">
            <Card.Header className="border-bottom">
              <h5 className="mb-0">Son Üyeler</h5>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table className="table-dark table-hover mb-0" style={{ fontSize: '0.85rem' }}>
                  <thead>
                    <tr>
                      <th className="border-0">Sıra</th>
                      <th className="border-0">Kullanıcı</th>
                      <th className="border-0">Durum</th>
                      <th className="border-0">Kredi</th>
                      <th className="border-0">İşlem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lastMembers.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="text-center p-4">Henüz üye yok.</td>
                      </tr>
                    ) : (
                      lastMembers.map((member) => (
                        <tr key={member.userId || member.id}>
                          <td>{member.id}</td>
                          <td>
                            <div className="d-flex align-items-center">
                              {member.profileImageUrl ? (
                                <img
                                  src={member.profileImageUrl}
                                  alt={member.name}
                                  className="rounded-circle"
                                  style={{ width: 32, height: 32, marginRight: 8, objectFit: 'cover' }}
                                />
                              ) : (
                                <div className="rounded-circle bg-secondary" style={{ width: 32, height: 32, marginRight: 8 }}></div>
                              )}
                              {member.name}
                              {member.isPremium && (
                                <Badge bg="warning" text="dark" className="ms-2" style={{ fontSize: '0.7rem' }}>VİP</Badge>
                              )}
                            </div>
                          </td>
                          <td>
                            {(() => {
                              // Backend isPresent + same 5dk lastSeen TTL (sticky isOnline alone is not enough)
                              const lastSeen = member.lastSeen;
                              const lastSeenMs = lastSeen
                                ? (lastSeen.toDate ? lastSeen.toDate() : new Date(lastSeen)).getTime()
                                : 0;
                              const recentlyActive = lastSeenMs && (Date.now() - lastSeenMs < 5 * 60 * 1000);
                              const isOnline = member.isOnline === true && recentlyActive;
                              return (
                                <FontAwesomeIcon
                                  icon={isOnline ? faCheckCircle : faTimesCircle}
                                  className={isOnline ? "text-success" : "text-danger"}
                                  title={isOnline ? "Çevrimiçi" : "Çevrimdışı"}
                                />
                              );
                            })()}
                          </td>
                          <td>{member.credit}</td>
                          <td>
                            <div className="d-flex gap-1 flex-wrap">
                              <Button
                                size="sm"
                                variant="info"
                                onClick={(e) => {
                                  e.preventDefault();
                                  openMessageModal(memberUid(member), member.name);
                                }}
                              >
                                <FontAwesomeIcon icon={faPhone} className="me-1" />
                                Mesaj
                              </Button>
                              <Button
                                size="sm"
                                variant="primary"
                                onClick={(e) => {
                                  e.preventDefault();
                                  openCallModal(memberUid(member), member.name);
                                }}
                              >
                                <FontAwesomeIcon icon={faPhone} className="me-1" />
                                Arama
                              </Button>
                              <Button
                                size="sm"
                                variant="success"
                                title="Jeton ekle/sil"
                                onClick={(e) => {
                                  e.preventDefault();
                                  openCreditsModal(memberUid(member), member.name, member.credit);
                                }}
                              >
                                <FontAwesomeIcon icon={faDollarSign} />
                              </Button>
                              <Button
                                size="sm"
                                variant="warning"
                                title="VIP ekle/kaldır"
                                onClick={(e) => {
                                  e.preventDefault();
                                  openPremiumModal(memberUid(member), member.name, member.isPremium);
                                }}
                              >
                                <FontAwesomeIcon icon={faCrown} />
                              </Button>
                              <Button
                                size="sm"
                                variant="danger"
                                title="Ban"
                                onClick={(e) => {
                                  e.preventDefault();
                                  openBanModal(memberUid(member), member.name, false);
                                }}
                              >
                                <FontAwesomeIcon icon={faBan} />
                              </Button>
                              <Button
                                size="sm"
                                variant="secondary"
                                title="Bildirim gönder"
                                onClick={(e) => {
                                  e.preventDefault();
                                  openNotificationModal(memberUid(member), member.name);
                                }}
                              >
                                <FontAwesomeIcon icon={faBell} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} lg={6} className="mb-4">
          <Card border="0" className="shadow-sm">
            <Card.Header className="border-bottom d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Çevrim İçi Üyeler</h5>
              <Button
                variant="warning"
                size="sm"
                onClick={handleResetOnlineUsers}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="fa-spin me-1" />
                    Sıfırlanıyor...
                  </>
                ) : (
                  'Online Üyeleri Sıfırla'
                )}
              </Button>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table className="table-dark table-hover mb-0" style={{ fontSize: '0.85rem' }}>
                  <thead>
                    <tr>
                      <th className="border-0">Sıra</th>
                      <th className="border-0">Kullanıcı</th>
                      <th className="border-0">Durum</th>
                      <th className="border-0">Kredi</th>
                      <th className="border-0">İşlem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {onlineMembers.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="text-center p-4">Online üye yok.</td>
                      </tr>
                    ) : (
                      onlineMembers.map((member, index) => (
                        <tr key={member.id}>
                          <td>{(currentOnlinePage - 1) * itemsPerPage + index + 1}</td>
                          <td>
                            <div className="d-flex align-items-center">
                              {member.profileImageUrl ? (
                                <img
                                  src={member.profileImageUrl}
                                  alt={member.name}
                                  className="rounded-circle"
                                  style={{ width: 32, height: 32, marginRight: 8, objectFit: 'cover' }}
                                />
                              ) : (
                                <div className="rounded-circle bg-secondary" style={{ width: 32, height: 32, marginRight: 8 }}></div>
                              )}
                              {member.name}
                              {member.isPremium && (
                                <Badge bg="warning" text="dark" className="ms-2" style={{ fontSize: '0.7rem' }}>VİP</Badge>
                              )}
                            </div>
                          </td>
                          <td>
                            <FontAwesomeIcon
                              icon={faCheckCircle}
                              className="text-success"
                            />
                          </td>
                          <td>{member.credit}</td>
                          <td>
                            <div className="d-flex gap-1 flex-wrap">
                              <Button
                                size="sm"
                                variant="info"
                                onClick={(e) => {
                                  e.preventDefault();
                                  openMessageModal(memberUid(member), member.name);
                                }}
                              >
                                <FontAwesomeIcon icon={faPhone} className="me-1" />
                                Mesaj
                              </Button>
                              <Button
                                size="sm"
                                variant="primary"
                                onClick={(e) => {
                                  e.preventDefault();
                                  openCallModal(memberUid(member), member.name);
                                }}
                              >
                                <FontAwesomeIcon icon={faPhone} className="me-1" />
                                Arama
                              </Button>
                              <Button
                                size="sm"
                                variant="success"
                                title="Jeton ekle/sil"
                                onClick={(e) => {
                                  e.preventDefault();
                                  openCreditsModal(memberUid(member), member.name, member.credit);
                                }}
                              >
                                <FontAwesomeIcon icon={faDollarSign} />
                              </Button>
                              <Button
                                size="sm"
                                variant="warning"
                                title="VIP ekle/kaldır"
                                onClick={(e) => {
                                  e.preventDefault();
                                  openPremiumModal(memberUid(member), member.name, member.isPremium);
                                }}
                              >
                                <FontAwesomeIcon icon={faCrown} />
                              </Button>
                              <Button
                                size="sm"
                                variant="danger"
                                title="Ban"
                                onClick={(e) => {
                                  e.preventDefault();
                                  openBanModal(memberUid(member), member.name, false);
                                }}
                              >
                                <FontAwesomeIcon icon={faBan} />
                              </Button>
                              <Button
                                size="sm"
                                variant="secondary"
                                title="Bildirim gönder"
                                onClick={(e) => {
                                  e.preventDefault();
                                  openNotificationModal(memberUid(member), member.name);
                                }}
                              >
                                <FontAwesomeIcon icon={faBell} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
              {totalOnlinePages > 1 && (
                <div className="d-flex justify-content-center p-3 bg-dark">
                  <Pagination className="mb-0" size="sm">
                    {Array.from({ length: totalOnlinePages }, (_, i) => {
                      const page = i + 1;
                      return (
                        <Pagination.Item
                          key={page}
                          active={currentOnlinePage === page}
                          onClick={() => setCurrentOnlinePage(page)}
                        >
                          {page}
                        </Pagination.Item>
                      );
                    })}
                  </Pagination>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>


      {/* Modals */}
      <Modal show={showCreditsModal} onHide={() => {
        setShowCreditsModal(false);
        setModalError(null);
      }} onEntered={() => creditsInputRef.current?.focus()}>
        <Modal.Header closeButton>
          <Modal.Title>Jeton Ekle/Sil - {targetUserName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalError && <Alert variant="danger">{modalError}</Alert>}
          <Form.Group>
            <Form.Label>
              Jeton Değişikliği (Mevcut: {targetUserCredits})
              <br />
              <small className="text-muted">Pozitif değer ekler, negatif değer çıkarır</small>
            </Form.Label>
            <Form.Control
              type="number"
              ref={creditsInputRef}
              value={creditsInput}
              onChange={(e) => { const val = (e.target || e.currentTarget || {}).value || ''; setCreditsInput(val); }}
              placeholder="Örn: 100 veya -50"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleUpdateCredits();
                }
              }}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowCreditsModal(false);
            setModalError(null);
          }}>
            İptal
          </Button>
          <Button variant="primary" onClick={handleUpdateCredits}>
            Kaydet
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showPremiumModal} onHide={() => {
        setShowPremiumModal(false);
        setModalError(null);
      }} onEntered={() => premiumInputRef.current?.focus()}>
        <Modal.Header closeButton>
          <Modal.Title>VIP Ekle/Kaldır - {targetUserName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalError && <Alert variant="danger">{modalError}</Alert>}
          <Alert variant={targetUserIsPremium ? 'warning' : 'secondary'} className="py-2">
            Durum: {targetUserIsPremium ? 'VIP aktif' : 'VIP yok'}
          </Alert>
          <Form.Group>
            <Form.Label>VIP Gün Sayısı (0 = VIP'i kaldır)</Form.Label>
            <Form.Control
              type="number"
              ref={premiumInputRef}
              value={premiumDaysInput}
              onChange={(e) => { const val = (e.target || e.currentTarget || {}).value || ''; setPremiumDaysInput(val); }}
              placeholder="30"
              min="0"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleUpdatePremium();
                }
              }}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowPremiumModal(false);
            setModalError(null);
          }}>
            İptal
          </Button>
          {targetUserIsPremium && (
            <Button variant="outline-danger" onClick={() => handleUpdatePremium(0)}>
              VIP Kaldır
            </Button>
          )}
          <Button variant="warning" onClick={() => handleUpdatePremium()}>
            Kaydet
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showBanModal} onHide={() => {
        setShowBanModal(false);
        setModalError(null);
      }}>
        <Modal.Header closeButton>
          <Modal.Title>{targetUserIsBanned ? 'Banı Kaldır' : 'Kullanıcıyı Banla'} - {targetUserName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalError && <Alert variant="danger">{modalError}</Alert>}
          {targetUserIsBanned ? (
            <p>Bu kullanıcının banını kaldırmak istediğinizden emin misiniz?</p>
          ) : (
            <p>Bu kullanıcıyı banlamak istediğinizden emin misiniz? Banlanan kullanıcılar uygulamaya giriş yapamaz.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowBanModal(false);
            setModalError(null);
          }}>
            İptal
          </Button>
          <Button variant="danger" onClick={handleBanUser}>
            {targetUserIsBanned ? 'Evet, Banı Kaldır' : 'Evet, Banla'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Mesaj Gönderme Modal */}
      <Modal show={showMessageModal} onHide={() => {
        setShowMessageModal(false);
        setModalError(null);
        setMessageText("");
        setFakeAccountSearch("");
      }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Mesaj Gönder - {targetUserName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalError && <Alert variant="danger">{modalError}</Alert>}

          <Form.Group className="mb-3">
            <Form.Label><strong>Mesajı Gönderen Kişi</strong></Form.Label>
            <div className="mb-2">
              <Form.Label className="text-muted">Hesap Seçin</Form.Label>
            </div>
            <Form.Control
              type="text"
              placeholder="Fake hesap ara..."
              value={fakeAccountSearch}
              onChange={(e) => setFakeAccountSearch(e.target.value)}
              className="mb-2"
            />
            <Form.Select
              value={selectedFakeAccountId || ""}
              onChange={(e) => setSelectedFakeAccountId(e.target.value)}
              required
            >
              <option value="">Fake hesap seçin...</option>
              {fakeAccounts
                .filter(account =>
                  fakeAccountSearch === "" ||
                  account.name.toLowerCase().includes(fakeAccountSearch.toLowerCase())
                )
                .map(account => (
                  <option key={account.id} value={account.id}>
                    {account.name}
                  </option>
                ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label><strong>Mesaj</strong></Form.Label>
            <Form.Control
              as="textarea"
              rows={6}
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              placeholder="Mesaj..."
              required
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowMessageModal(false);
              setModalError(null);
              setMessageText("");
              setFakeAccountSearch("");
            }}
            disabled={sendingMessage}
          >
            İptal
          </Button>
          <Button
            variant="primary"
            onClick={() => handleSendMessage(false)}
            disabled={sendingMessage || !selectedFakeAccountId || !messageText.trim()}
          >
            {sendingMessage ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="fa-spin me-2" />
                Gönderiliyor...
              </>
            ) : (
              'Gönder'
            )}
          </Button>
          <Button
            variant="info"
            onClick={() => handleSendMessage(true)}
            disabled={sendingMessage || !selectedFakeAccountId || !messageText.trim()}
          >
            {sendingMessage ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="fa-spin me-2" />
                Gönderiliyor...
              </>
            ) : (
              "Gönder ve Chat'e git"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Arama Başlatma Modal */}
      <Modal show={showCallModal} onHide={() => {
        setShowCallModal(false);
        setModalError(null);
        setCallType("recorded_voice");
      }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Arama Başlat - {targetUserName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalError && <Alert variant="danger">{modalError}</Alert>}

          <Form.Group className="mb-3">
            <Form.Label><strong>Aramayı Başlatan Hesap</strong></Form.Label>
            <div className="mb-2">
              <Form.Label className="text-muted">Fake Hesap Seçin</Form.Label>
            </div>
            <Form.Control
              type="text"
              placeholder="Fake hesap ara..."
              value={fakeAccountSearch}
              onChange={(e) => {
                setFakeAccountSearch(e.target.value);
              }}
              className="mb-2"
            />
            <Form.Select
              value={selectedFakeAccountId || ""}
              onChange={async (e) => {
                const fakeId = e.target.value;
                setSelectedFakeAccountId(fakeId);
                if (fakeId && (callType === 'recorded_voice' || callType === 'recorded_video')) {
                  await loadRecordings(fakeId);
                }
              }}
              required
            >
              <option value="">Fake hesap seçin...</option>
              {fakeAccounts
                .filter(account =>
                  fakeAccountSearch === "" ||
                  account.name.toLowerCase().includes(fakeAccountSearch.toLowerCase())
                )
                .map(account => (
                  <option key={account.id} value={account.id}>
                    {account.name}
                  </option>
                ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label><strong>Arama Türü</strong></Form.Label>
            <Form.Select
              value={callType}
              onChange={async (e) => {
                const newCallType = e.target.value;
                setCallType(newCallType);
                // Eğer recorded call seçildiyse ve fake hesap seçiliyse kayıtları yükle
                if ((newCallType === 'recorded_voice' || newCallType === 'recorded_video') && selectedFakeAccountId) {
                  await loadRecordings(selectedFakeAccountId);
                }
              }}
              required
            >
              <option value="normal_voice">Normal Sesli Arama</option>
              <option value="recorded_voice">Sesli Arama (Kayıtlı)</option>
              <option value="recorded_video">Videolu Arama (Kayıtlı)</option>
            </Form.Select>
          </Form.Group>

          {loadingRecordings && (
            <div className="text-center mb-3">
              <Spinner animation="border" size="sm" className="me-2" />
              Kayıtlar yükleniyor...
            </div>
          )}

          {callType === 'normal_voice' && (
            <Form.Group className="mb-3">
              <Alert variant="info">
                Normal sesli arama başlatılacak. Kayıt gerekmez.
              </Alert>
            </Form.Group>
          )}

          {callType === 'recorded_voice' && (
            <Form.Group className="mb-3">
              <Form.Label><strong>Ses Kayıtları</strong></Form.Label>
              {audioRecordings.length === 0 ? (
                <Alert variant="warning">
                  Bu fake hesapta ses kaydı bulunamadı. Lütfen önce ses kaydı ekleyin.
                </Alert>
              ) : audioRecordings.length === 1 ? (
                <Alert variant="info">
                  Kullanılacak kayıt: {audioRecordings[0].title || 'İsimsiz'}
                </Alert>
              ) : (
                <Alert variant="info">
                  Toplam {audioRecordings.length} ses kaydı var. İlk kayıt kullanılacak.
                </Alert>
              )}
            </Form.Group>
          )}

          {callType === 'recorded_video' && (
            <Form.Group className="mb-3">
              <Form.Label><strong>Video Kayıtları</strong></Form.Label>
              {videoRecordings.length === 0 ? (
                <Alert variant="warning">
                  Bu fake hesapta video kaydı bulunamadı. Lütfen önce video kaydı ekleyin.
                </Alert>
              ) : videoRecordings.length === 1 ? (
                <Alert variant="info">
                  Kullanılacak kayıt: {videoRecordings[0].title || 'İsimsiz'}
                </Alert>
              ) : (
                <Alert variant="info">
                  Toplam {videoRecordings.length} video kaydı var. İlk kayıt kullanılacak.
                </Alert>
              )}
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowCallModal(false);
              setModalError(null);
              setCallType("recorded_voice");
            }}
          >
            İptal
          </Button>
          <Button
            variant="primary"
            onClick={handleStartCall}
            disabled={!selectedFakeAccountId || loadingRecordings ||
              (callType === 'recorded_voice' && audioRecordings.length === 0) ||
              (callType === 'recorded_video' && videoRecordings.length === 0)}
          >
            Arama Başlat
          </Button>
        </Modal.Footer>
      </Modal>

      <SendUserNotificationModal
        show={showNotificationModal}
        onHide={() => setShowNotificationModal(false)}
        targetUserId={targetUserId}
        targetUserName={targetUserName}
      />
    </>
  );
};
